import { LIST_MSG, PAYMENT_METHOD_LABEL, PAYMENT_TAB_LABEL, PaymentTab, type PaymentList } from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListFilterBar, useListFilters } from "../../components/ListFilters.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { usePageName } from "../../components/BackButton.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText, Help } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { yen } from "../../lib/num.js";

import { ymdFull, ymdShort } from "../../lib/dates.js";
export const PAYMENTS_KEY = ["payments"] as const;
/** #302（A4）：「すべて」を先頭に。開いたときは今までどおり「未消込」（営業力ルール9） */
const TABS: PaymentTab[] = ["all", "unallocated", "month"];

/**
 * 入金の一覧（部品 payments、営業力 E1、../eigyouryoku/docs/08 §5 → #302 一覧の型）。
 *   タブ：すべて／未消込（既定）／今月。まだ消し込んでいない入金はどのタブでも一番上（営業力ルール9）
 *   列：入金日・{customer}・入金額・方法・請求書・未消込額（#302 A1）
 *   期間・{customer}の絞りと 絞った結果の件数・入金額・未消込額（#302 A6。API が from・to・partner・q で絞る＝タブの数も）。
 *   題の右の「未消込 n件 ○円」は絞りに関わらず会社の全部
 */
export function PaymentsPage() {
  const L = useLabels();
  usePageName("入金");
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const lf = useListFilters();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const parsed = PaymentTab.safeParse(sp.get("tab"));
  const tab: PaymentTab = parsed.success ? parsed.data : "unallocated";
  const qs = [`tab=${tab}`, lf.range.from ? `from=${lf.range.from}` : "", lf.range.to ? `to=${lf.range.to}` : "", lf.client ? `partner=${lf.client}` : "", qApplied ? `q=${encodeURIComponent(qApplied)}` : ""].filter(Boolean).join("&");
  const query = useQuery({ queryKey: [...PAYMENTS_KEY, tab, lf.range.from ?? "", lf.range.to ?? "", lf.client, qApplied], queryFn: () => api<PaymentList>("GET", `/payments?${qs}`), placeholderData: keepPreviousData });
  const d = query.data;
  const setTab = (k: PaymentTab) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (k === "unallocated") n.delete("tab");
        else n.set("tab", k);
        return n;
      },
      { replace: true },
    );
  const all = d?.unallocatedAll ?? (d ? { count: d.counts.unallocated, total: d.unallocatedTotal } : null);
  const filtered = lf.active || qApplied !== "";
  return (
    <ListHead
      title={
        <>
          入金
          <Help testId="help-payments" text={`${L.customer}からの入金を登録して、発行済みの請求書に消込します。1回の振込で何枚分でも消込できます。消込していない額は「未消込」に残り、請求書を発行したら、入金の画面の「修正」から消込できます。`} />
          {/* 消し込んでいない入金（#168：帯 → 見出しの右のチップ）。#302：絞りに関わらず会社の全部 */}
          {all && all.count > 0 ? (
            <span className="chip ai" data-testid="unallocated-band">
              未消込 {all.count}件 <span className="num">{yen(all.total)}</span> 円
            </span>
          ) : null}
        </>
      }
    >
      {query.isError ? <ErrorText>{messageOf(query.error)}</ErrorText> : null}
      <div className="lcard">
        <ListTabs
          id="pl"
          label="入金を絞る"
          tabs={TABS.map((k) => ({ key: k, label: PAYMENT_TAB_LABEL[k], count: d ? d.counts[k] : "…" }))}
          value={tab}
          onChange={setTab}
          filter={<FilterInput value={q} onChange={setQ} placeholder={t(LIST_MSG.searchPayment)} testId="pl-search" />}
          create={
            <Link className="btn primary" to="/payments/new" data-testid="payment-new">
              <Icon k="plus" />
              入金登録
            </Link>
          }
        />
        <ListFilterBar
          f={lf}
          clientName={lf.client ? d?.items.find((x) => x.partnerId === lf.client)?.partnerName : undefined}
          sum={d ? { count: d.sum?.count ?? d.items.length, amount: d.sum?.amount ?? d.items.reduce((a, x) => a + (x.voided ? 0 : x.amount), 0), unallocated: d.sum?.unallocated ?? d.items.reduce((a, x) => a + Math.max(0, x.unallocated), 0) } : null}
        />
        <div className="tbl-wrap ltab-panel" {...listTabPanel("pl", tab)}>
          <table className="list" data-testid="payments-table">
            <thead>
              <tr>
                <th>{LIST_MSG.colPayDate}</th>
                <th>{L.customer}</th>
                <th className="amt">{LIST_MSG.colAmount}</th>
                <th>{LIST_MSG.colMethod}</th>
                <th>{LIST_MSG.colInvoice}</th>
                <th className="amt">{LIST_MSG.colUnallocated}</th>
              </tr>
            </thead>
            <tbody>
              {(d?.items ?? []).map((x) => (
                <tr key={x.id} className="r" tabIndex={0} onClick={() => nav(`/payments/${x.id}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/payments/${x.id}`)} data-testid="payment-row">
                  <td className="num ldate" title={ymdFull(x.receivedOn)}>{ymdShort(x.receivedOn)}</td>
                  <td title={x.partnerFullName ?? x.partnerName}>
                    <b className="lname">{x.partnerName}</b>
                    {x.voided ? <span className="chip" style={{ marginLeft: 8 }}>削除済</span> : null}
                    {x.hasReceipt ? <span className="chip" style={{ marginLeft: 8 }}>領収書あり</span> : null}
                  </td>
                  <td className="amt num">{yen(x.amount)}</td>
                  <td>{PAYMENT_METHOD_LABEL[x.method]}</td>
                  <td className="num">{x.allocatedTo || <span className="muted">—</span>}</td>
                  <td className="amt num">{x.unallocated > 0 ? <b data-testid="payment-unallocated">{yen(x.unallocated)}</b> : <span className="muted">—</span>}</td>
                </tr>
              ))}
              {d && d.items.length === 0 ? (
                <tr className="emptyrows">
                  <td colSpan={6}>
                    {filtered ? "条件に合う入金はありません。" : tab === "unallocated" ? "未消込の入金はありません。" : tab === "month" ? "今月の入金はありません。" : "入金の登録はありません。「入金登録」から登録します。"}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </ListHead>
  );
}
